import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { useFeedback } from '../contexts/FeedbackContext';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import { isFormDirty, discardChangesConfirm } from '../utils/formDirty';
import { requestErrorMessage, saveErrorMessage } from '../utils/saveErrors';
import { eventTypeService } from '../services/eventTypeService';
import { EventType } from '../types/eventType';

const EventTypeManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const confirm = useConfirm();
  const { showSuccess } = useFeedback();
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [selectedEventType, setSelectedEventType] = useState<EventType | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'create' | 'edit' | 'view'>('view');
  /**
   * [7. javító kör — validátor F3, 2026-10-04] A mentés hibája a NYITVA maradó dialógusban is
   * (a `CoachPaymentManagement` `failCreate` mintája). KORÁBBAN csak a lap tetejei sávba került,
   * ami a modális dialógus mögött takarásban volt: a „Létrehozás"/„Mentés" után semmi nem látszott.
   */
  const [dialogError, setDialogError] = useState<string>('');

  const predefinedColors = [
    '#4CAF50', '#2196F3', '#F44336', '#FF9800', 
    '#9C27B0', '#795548', '#607D8B', '#E91E63',
    '#00BCD4', '#8BC34A', '#FFC107', '#673AB7'
  ];

  useEffect(() => {
    loadEventTypes();
  }, []);

  const loadEventTypes = async () => {
    try {
      setLoading(true);
      setError('');
      const eventTypeData = await eventTypeService.getAllEventTypes();
      setEventTypes(eventTypeData);
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Hiba történt az eseménytípusok betöltésekor'));
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (type: typeof dialogType, eventType?: EventType) => {
    setDialogType(type);
    setSelectedEventType(eventType || null);
    setDialogError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedEventType(null);
    setDialogError('');
  };

  const handleEventTypeAction = async (action: 'create' | 'edit', data?: any) => {
    try {
      setError('');
      setDialogError('');

      switch (action) {
        case 'create':
          await eventTypeService.createEventType(data);
          showSuccess('Az eseménytípus létrehozva.');
          break;
        case 'edit':
          if (selectedEventType) {
            await eventTypeService.updateEventType(selectedEventType.eventTypeId, data);
            showSuccess('Az eseménytípus módosítva.');
          }
          break;
      }

      closeDialog();
      await loadEventTypes();
    } catch (err: any) {
      // [3. javító kör, F-1] lejáratkor (újra-belépő ablak) a „megmaradtak" mondat, nem a nyers 401
      const message = saveErrorMessage(err, 'Hiba történt a művelet során');
      setError(message);
      // [7. javító kör, F3] a dialógusban is (a lap tetejei sáv a dialógus mögött van)
      setDialogError(message);
    }
  };

  const handleDeleteEventType = async (eventType: EventType) => {
    const confirmed = await confirm({
      title: 'Eseménytípus törlése',
      message: (
        <>
          Biztosan törölni szeretnéd a <strong>{eventType.name}</strong> eseménytípust?
          Ez deaktiválja az eseménytípust a rendszerben.
        </>
      ),
      confirmLabel: 'Törlés',
      destructive: true,
    });
    if (!confirmed) return;

    try {
      setError('');
      await eventTypeService.deleteEventType(eventType.eventTypeId);
      showSuccess('Az eseménytípus törölve.');
      await loadEventTypes();
    } catch (err: any) {
      setError(requestErrorMessage(err, 'Hiba történt a művelet során'));
    }
  };

  const isAdmin = currentUser?.userRole === 'Administrator';

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Nincs jogosultságod az eseménytípusok kezeléséhez.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Eseménytípus kezelés
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Eseménytípusok ({eventTypes.length})
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openDialog('create')}
          >
            Új eseménytípus
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Név</TableCell>
              <TableCell>Megjelenítési Név</TableCell>
              <TableCell>Szín</TableCell>
              <TableCell>Leírás</TableCell>
              <TableCell>Állapot</TableCell>
              <TableCell align="right">Műveletek</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : eventTypes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Nincs találat
                </TableCell>
              </TableRow>
            ) : (
              eventTypes.map((eventType) => (
                <TableRow key={eventType.eventTypeId}>
                  <TableCell>{eventType.name}</TableCell>
                  <TableCell>{eventType.displayName}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          backgroundColor: eventType.color,
                          borderRadius: '50%',
                          border: '2px solid #ddd',
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {eventType.color}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{eventType.description || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={eventType.isActive ? 'Aktív' : 'Inaktív'}
                      color={eventType.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Megtekintés">
                      <IconButton
                        size="small"
                        onClick={() => openDialog('view', eventType)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Szerkesztés">
                      <IconButton
                        size="small"
                        onClick={() => openDialog('edit', eventType)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Törlés">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteEventType(eventType)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* EventType Dialog */}
      <EventTypeDialog
        open={dialogOpen}
        onClose={closeDialog}
        type={dialogType}
        eventType={selectedEventType}
        onAction={handleEventTypeAction}
        predefinedColors={predefinedColors}
        error={dialogError}
      />
    </Box>
  );
};

interface EventTypeDialogProps {
  open: boolean;
  onClose: () => void;
  type: 'create' | 'edit' | 'view';
  eventType: EventType | null;
  onAction: (action: 'create' | 'edit', data?: any) => void;
  predefinedColors: string[];
  /** [7. javító kör, F3] a mentés hibája — a dialógusban látszik, nem csak a mögötte lévő lapon */
  error?: string;
}

const EventTypeDialog: React.FC<EventTypeDialogProps> = ({
  open,
  onClose,
  type,
  eventType,
  onAction,
  predefinedColors,
  error,
}) => {
  const confirm = useConfirm();
  const [formData, setFormData] = useState<any>({
    name: '',
    displayName: '',
    color: '#4CAF50',
    description: '',
    isActive: true,
    displayOrder: 1,
  });
  /**
   * [Web UX 2. csomag – mentetlen-védelem (8.1), 2026-10-03] A MEGNYITÁSKORI űrlap az alapvonal.
   * Lelet: `EventTypeManagement.tsx:342` (`<Dialog onClose={onClose}>`) és `:440` (Bezárás) —
   * a háttér-kattintás/Esc kérdés nélkül eldobta a beírt nevet, leírást, színt.
   * `null` = zárva → soha nem piszkos.
   */
  const [baseline, setBaseline] = useState<any>(null);

  // `open` is függőség: korábban két egymás utáni „Új” megnyitás között (type és eventType
  // változatlan) az effekt nem futott le, így az ELVETETT adat visszajött a második megnyitáskor.
  useEffect(() => {
    if (!open) {
      setBaseline(null);
      return;
    }
    let initial: any = null;
    if (eventType && (type === 'edit' || type === 'view')) {
      initial = {
        name: eventType.name,
        displayName: eventType.displayName,
        color: eventType.color,
        description: eventType.description || '',
        isActive: eventType.isActive,
        displayOrder: eventType.displayOrder,
      };
    } else if (type === 'create') {
      initial = {
        name: '',
        displayName: '',
        color: '#4CAF50',
        description: '',
        isActive: true,
        displayOrder: 1,
      };
    }
    if (initial) {
      setFormData(initial);
      setBaseline(initial);
    }
  }, [open, eventType, type]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const submitData = {
      ...formData,
      displayOrder: Number(formData.displayOrder),
    };
    onAction(type as 'create' | 'edit', submitData);
  };

  const getTitle = () => {
    switch (type) {
      case 'create': return 'Új eseménytípus létrehozása';
      case 'edit': return 'Eseménytípus szerkesztése';
      case 'view': return 'Eseménytípus adatai';
      default: return '';
    }
  };

  const isViewMode = type === 'view';

  const isDirty = open && !isViewMode && isFormDirty(baseline, formData);
  useUnsavedChanges(isDirty, type === 'create' ? 'Új eseménytípus' : 'Eseménytípus szerkesztése');

  /** Háttér-kattintás, Esc és „Bezárás”: beírt adat esetén a közös ablak kérdez. */
  const handleClose = async () => {
    if (isDirty && !(await confirm(discardChangesConfirm('eseménytípus űrlapja')))) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{getTitle()}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }} data-testid="event-type-dialog-error">
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Név *"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              disabled={isViewMode}
              required
            />
            <TextField
              fullWidth
              label="Megjelenítési név *"
              value={formData.displayName}
              onChange={(e) => handleInputChange('displayName', e.target.value)}
              disabled={isViewMode}
              required
            />
            <TextField
              fullWidth
              label="Leírás"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              disabled={isViewMode}
              multiline
              rows={2}
            />
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Szín kiválasztása
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {predefinedColors.map((color) => (
                  <Box
                    key={color}
                    onClick={() => !isViewMode && handleInputChange('color', color)}
                    sx={{
                      width: 36,
                      height: 36,
                      backgroundColor: color,
                      borderRadius: '50%',
                      border: formData.color === color ? '3px solid #333' : '2px solid #ddd',
                      cursor: isViewMode ? 'default' : 'pointer',
                      '&:hover': !isViewMode ? {
                        transform: 'scale(1.1)',
                      } : {},
                      transition: 'all 0.2s',
                    }}
                  />
                ))}
              </Box>
              <TextField
                fullWidth
                label="Egyéni szín (hex kód)"
                value={formData.color}
                onChange={(e) => handleInputChange('color', e.target.value)}
                disabled={isViewMode}
                placeholder="#4CAF50"
                InputProps={{
                  startAdornment: (
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        backgroundColor: formData.color,
                        borderRadius: '50%',
                        mr: 1,
                        border: '1px solid #ddd',
                      }}
                    />
                  ),
                }}
              />
            </Box>
            <TextField
              fullWidth
              label="Sorrend"
              type="number"
              value={formData.displayOrder}
              onChange={(e) => handleInputChange('displayOrder', e.target.value)}
              disabled={isViewMode}
              inputProps={{ min: 0 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  disabled={isViewMode}
                />
              }
              label="Aktív (megjelenik a kiválasztási lehetőségek között)"
            />
          </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          Bezárás
        </Button>
        {!isViewMode && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
          >
            {type === 'create' ? 'Létrehozás' : 'Mentés'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EventTypeManagement;